<template>
  <div class="tracking-consent-popup-wrapper" :class="{ show: showPopup }" tabindex="-1" :aria-hidden="!showPopup" role="dialog" aria-modal="true" aria-label="Tracking Consent">
    <div class="tracking-consent-popup vcore_container">
      <div class="vcore_row">
        <button
          @click="postpone"
          class="tracking-consent-popup_close"
          aria-label="close-tracking"
          @keydown.tab="closeTab"
          ref="closeButton" :tabindex="showPopup ? 0 : -1"
        >
          <i class="vcore_icon icon-close"></i>
        </button>
        <div class="vcore_col--xs--12 vcore_col--md--7">
          <div class="tracking-consent-popup_text" ref="trackingText">
            We use cookies on this site - they help us provide you with a better
            online experience.
            <a href="https://www.commercebank.com/online-privacy-policy" target="_blank" class="tracking-consent-popup_learn-more vcore_link" :tabindex="showPopup ? 0 : -1"
              >Learn more.</a
            >
          </div>
        </div>
        <div
          class="tracking-consent-popup_btn-wrapper vcore_col--xs--12 vcore_col--md--5"
        >
          <div class="vcore_row">
            <div class="vcore_col--sm--6 vcore_col--xs--12 vcore_push--sm--6">
              <Button
                @click.native="accept"
                variant="secondary"
                color="blue"
                block
                aria-label="accept-tracking" :tabindex="showPopup ? 0 : -1"
                >Accept</Button
              >
            </div>
            <div class="vcore_col--sm--6 vcore_col--xs--12 vcore_pull--sm--6 tracking-consent-popup_decline-btn-wrapper">
              <Button
                @click.native="decline"
                @keydown.tab.native="optOutTab"
                ref="optOut"
                variant="flat"
                color="blue"
                aria-label="decline-tracking" :tabindex="showPopup ? 0 : -1"
                >Opt-out</Button
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "./Button";
import Cookies from "js-cookie";
import moment from "moment";

export default {
  name: "TrackingConsentPopup",
  components: {
    Button
  },
  props: {
    eventBus: {
      type: Object,
      optional: true
    }
  },
  data() {
    return {
      showPopup: false,
      supportsLocalStorage: true,
      status: null,
      emmitter: this.eventBus ? this.eventBus : this
    };
  },
  methods: {
    init() {
      let cookie = this.getCookieStatus();
      let visitedType;
      let expiration;
      if (this.supportsLocalStorage && cookie) {
        visitedType = cookie.type;
        expiration = cookie.expiresOn;
      } else {
        visitedType = cookie;
      }
      if (
        visitedType &&
        (visitedType === "accept" ||
          visitedType === "decline" ||
          visitedType === "postpone")
      ) {
        this.showPopup = false;
        this.emmitter.$emit("tracking", visitedType);
      }
      if (!visitedType || moment(expiration).isBefore(moment())) {
        this.removeCookie();
        setTimeout(() => {
          this.showPopup = true;
        }, 1000);
      }
      this.status = visitedType;
    },
    checkLocalStorageFunctionality() {
      // Check for availability of localStorage
      try {
        const test = "__check-localStorage";
        window.localStorage.setItem(test, test);
        window.localStorage.removeItem(test);
      } catch (e) {
        console.error(
          "Local storage is not supported, falling back to cookie use"
        );
        this.supportsLocalStorage = false;
      }
    },
    setCookieStatus(type) {
      if (this.supportsLocalStorage) {
        if (type === "accept") {
          localStorage.setItem(
            "cb-tracking-consent",
            JSON.stringify({
              type: "accept",
              expiresOn: moment()
                .add(1, "year")
                .valueOf()
            })
          );
        }
        if (type === "decline") {
          localStorage.setItem(
            "cb-tracking-consent",
            JSON.stringify({
              type: "decline",
              expiresOn: moment()
                .add(1, "day")
                .valueOf()
            })
          );
        }
        if (type === "postpone") {
          localStorage.setItem(
            "cb-tracking-consent",
            JSON.stringify({
              type: "postpone",
              expiresOn: moment()
                .add(1, "day")
                .valueOf()
            })
          );
        }
      } else {
        if (type === "accept") {
          Cookies.set("cb-tracking-consent", "accept", { expires: 365 });
        }
        if (type === "decline") {
          Cookies.set("cb-tracking-consent", "decline", { expires: 1 });
        }
        if (type === "postpone") {
          Cookies.set("cb-tracking-consent", "postpone", { expires: 1 });
        }
      }
    },
    getCookieStatus() {
      if (this.supportsLocalStorage) {
        return JSON.parse(localStorage.getItem("cb-tracking-consent"));
      } else {
        return Cookies.get("cb-tracking-consent");
      }
    },
    accept() {
      this.setCookieStatus("accept");
      this.status = "accept";
      this.showPopup = false;
      this.emmitter.$emit("tracking", "accept");
    },
    decline() {
      this.setCookieStatus("decline");
      this.status = "decline";
      this.showPopup = false;
      this.emmitter.$emit("tracking", "decline");
    },
    postpone() {
      this.setCookieStatus("postpone");
      this.status = "postpone";
      this.showPopup = false;
      this.emmitter.$emit("tracking", "postpone");
    },
    removeCookie() {
      localStorage.removeItem("cb-tracking-consent");
      this.status = null;
    },
    closeTab(e) {
      if (e.shiftKey) {
        e.preventDefault();
        this.$refs.optOut.$el.focus();
      }
    },
    optOutTab(e) {
      if (!e.shiftKey) {
        e.preventDefault();
        this.$refs.closeButton.focus();
      }
    }
  },
  mounted() {
    this.checkLocalStorageFunctionality();
    this.init();
  }
};
</script>

<style lang="scss" scoped>
.tracking-consent-popup-wrapper {
  position: fixed;
  bottom: -100%;
  left: 0;
  right: 0;
  transition: bottom 800ms ease-in-out;
  z-index: 999;

  &.show {
    bottom: 0;
  }
}

.tracking-consent-popup {
  position: relative;
  margin: 0 auto;
  padding: 35px 50px;
  background-color: $color_white;
  border-radius: 15px 15px 0 0;
  box-shadow: 0 0 16px 0 rgba(67, 69, 77, 0.41);
}

.tracking-consent-popup_close {
  position: absolute;
  top: 10px;
  right: 10px;
  font: inherit;
  font-size: 18px;
  -webkit-appearance: none;
  border: 0;
  padding: 0;
}

.tracking-consent-popup_text {
  line-height: 20px;
}

.tracking-consent-popup_learn-more {
  font-weight: bold;
}

.tracking-consent-popup_btn-wrapper {
  @media (max-width: $grid-breakpoint-md) {
    margin-top: 20px;
  }
}

.tracking-consent-popup_decline-btn-wrapper {
  text-align: right;

  @media (max-width: $grid-breakpoint-md) {
    text-align: center;
  }

  @media (max-width: $grid-breakpoint-sm) {
    margin-top: 10px;
  }
}
</style>
